import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../../api';
import { t } from '../../i18n';
import { showToast } from './toast';
import type { SelectOption } from './select';

interface Currency { code: string; name: string | null }

// The managed currency list rarely changes within a session, so fetch it once and
// share the promise across every <cadence-currency-select> instance (an event page
// renders three of them). `invalidateCurrencyCache()` clears it after an admin edits
// the list in Settings so the next picker reflects the change.
let cache: Promise<Currency[]> | null = null;
function loadCurrencies(): Promise<Currency[]> {
  if (!cache) cache = api.get<Currency[]>('/currencies').catch(() => []);
  return cache;
}
export function invalidateCurrencyCache(): void { cache = null; }

/**
 * <cadence-currency-select label="Currency" .value=${code}> — a currency picker
 * fed from the school's managed currency list. Replaces free-text currency inputs
 * so codes are always valid + uppercase. Emits `change` with the selected code.
 * If `value` is a legacy code not in the list, it's shown so it isn't silently lost.
 */
@customElement('cadence-currency-select')
export class CadenceCurrencySelect extends LitElement {
  @property() label = '';
  @property() value = '';
  @property() placeholder = '';
  // Opt-in: render an inline "Add a currency" control that POSTs to /currencies
  // (requires settings.edit) and selects the new code. Used by the setup wizard so a
  // fresh deployment isn't limited to the seeded list.
  @property({ type: Boolean }) allowAdd = false;
  @state() private options: SelectOption[] = [];
  @state() private adding = false;
  @state() private newCode = '';
  @state() private newName = '';
  @state() private saving = false;

  static styles = css`
    :host { display: block; }
    .add-link { background: none; border: none; padding: 4px 0; margin-top: 6px; color: var(--color-primary); cursor: pointer; font: inherit; font-size: 0.85rem; }
    .add-link:hover { text-decoration: underline; }
    /* Flex-wrap (not a fixed grid) so the row reflows inside a narrow parent cell
       instead of forcing it wider and pushing sibling fields out of the layout. */
    .add-row { display: flex; flex-wrap: wrap; gap: 8px; align-items: end; margin-top: 8px; }
    .add-row cadence-input { flex: 1 1 110px; min-width: 0; }
    .add-row cadence-button { flex: 0 0 auto; }
  `;

  connectedCallback(): void {
    super.connectedCallback();
    void this.refresh();
  }

  private async refresh() {
    const list = await loadCurrencies();
    const opts = list.map((c) => ({ value: c.code, label: c.name ? `${c.code} — ${c.name}` : c.code }));
    if (this.value && !opts.some((o) => o.value === this.value)) opts.unshift({ value: this.value, label: this.value });
    this.options = opts;
  }

  private onChange(e: CustomEvent) {
    this.value = e.detail;
    this.dispatchEvent(new CustomEvent('change', { detail: e.detail, bubbles: true, composed: true }));
  }

  private async addCurrency() {
    const code = this.newCode.trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(code)) { showToast(t('ui.currency.invalid'), 'error'); return; }
    this.saving = true;
    try {
      await api.post('/currencies', { code, name: this.newName.trim() || undefined });
      invalidateCurrencyCache();
      await this.refresh();
      this.adding = false; this.newCode = ''; this.newName = '';
      this.value = code;
      this.dispatchEvent(new CustomEvent('change', { detail: code, bubbles: true, composed: true }));
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.saving = false; }
  }

  render() {
    return html`
      <cadence-select label=${this.label || t('ui.currency.label')} placeholder=${this.placeholder}
        .options=${this.options} .value=${this.value} @change=${this.onChange}></cadence-select>
      ${this.allowAdd ? (this.adding ? this.renderAddRow() : html`
        <button type="button" class="add-link" @click=${() => (this.adding = true)}>+ ${t('ui.currency.add')}</button>`) : ''}
    `;
  }

  private renderAddRow() {
    return html`
      <div class="add-row">
        <cadence-input label=${t('ui.currency.code')} placeholder="EUR" maxlength="3"
          .value=${this.newCode} @input=${(e: CustomEvent) => (this.newCode = e.detail)}></cadence-input>
        <cadence-input label=${t('ui.currency.name')} placeholder=${t('ui.currency.namePlaceholder')}
          .value=${this.newName} @input=${(e: CustomEvent) => (this.newName = e.detail)}></cadence-input>
        <cadence-button ?loading=${this.saving} @click=${() => this.addCurrency()}>${t('ui.currency.addSave')}</cadence-button>
        <cadence-button variant="ghost" @click=${() => { this.adding = false; this.newCode = ''; this.newName = ''; }}>${t('common.actions.cancel')}</cadence-button>
      </div>
    `;
  }
}
